/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * In-memory cookie storage and CDU consent detection used by the cookie wrapper
 * when consent is pending or rejected. Data lives in RAM only.
 */

class InMemoryCookieStorage {
  private data: Map<string, { value: string; expires?: number }> = new Map();

  set(name: string, value: string, options?: { expires?: Date }): void {
    this.data.set(name, {
      value,
      expires: options?.expires?.getTime(),
    });
  }

  get(name: string): string | null {
    const stored = this.data.get(name);
    if (!stored) {
      return null;
    }

    if (stored.expires && stored.expires < Date.now()) {
      this.data.delete(name);
      return null;
    }

    return stored.value;
  }

  delete(name: string): void {
    this.data.delete(name);
  }

  clear(): void {
    this.data.clear();
  }

  getAll(): string[] {
    const result: string[] = [];
    const now = Date.now();

    this.data.forEach((data, name) => {
      if (data.expires && data.expires < now) {
        this.data.delete(name);
        return;
      }

      result.push(`${name}=${data.value}`);
    });

    return result;
  }
}

let inMemoryCookieStorage: InMemoryCookieStorage | null = null;

/**
 * Returns the process-wide in-memory cookie storage, creating it on first use.
 *
 * @returns The singleton {@link InMemoryCookieStorage} instance.
 */
export function getInMemoryCookieStorage(): InMemoryCookieStorage {
  if (!inMemoryCookieStorage) {
    inMemoryCookieStorage = new InMemoryCookieStorage();
  }
  return inMemoryCookieStorage;
}

/**
 * Persists all buffered in-memory cookies to real `document.cookie` (1-year
 * expiry) once CDU consent is accepted, then clears the buffer.
 */
export function migrateInMemoryToRealCookies(): void {
  console.log('[CookieWrapper] Migrating in-memory cookies to real cookies');

  const memory = getInMemoryCookieStorage();
  const cookies = memory.getAll();

  cookies.forEach((cookieString) => {
    const [name, value] = cookieString.split('=');
    if (name && value) {
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
      console.log(`[CookieWrapper] Migrated cookie: ${name}`);
    }
  });

  memory.clear();
  console.log('[CookieWrapper] In-memory cookie migration complete');
}

/**
 * Discards all buffered in-memory cookies (e.g. when consent is rejected).
 */
export function clearInMemoryCookieStorage(): void {
  getInMemoryCookieStorage().clear();
  console.log('[CookieWrapper] In-memory cookie storage cleared');
}

/**
 * Snapshots the buffered in-memory cookies as a name→value map (for debugging).
 *
 * @returns An object mapping cookie names to their current values.
 */
export function getInMemoryCookieStorageContents(): Record<string, string> {
  const memory = getInMemoryCookieStorage();
  const cookies = memory.getAll();
  const contents: Record<string, string> = {};

  cookies.forEach((cookieString) => {
    const [name, value] = cookieString.split('=');
    if (name && value) {
      contents[name] = value;
    }
  });

  return contents;
}

/**
 * Reads the consent status from the real `document.cookie`, if present.
 *
 * @returns The parsed status, or null when the cookie is absent or unparseable.
 */
function readDocumentConsentStatus(): string | null {
  try {
    const consentCookie = document.cookie
      .split(';')
      .find((cookie) => cookie.trim().startsWith('cdu_consent_v1='));
    if (!consentCookie) {
      return null;
    }
    const decodedValue = decodeURIComponent(consentCookie.split('=')[1]);
    return JSON.parse(decodedValue).status || 'pending';
  } catch (error) {
    return null;
  }
}

/**
 * Reads the consent status from in-memory cookie storage, if present.
 *
 * @returns The parsed status, or null when the entry is absent or unparseable.
 */
function readMemoryConsentStatus(): string | null {
  try {
    const memoryCookie = getInMemoryCookieStorage().get('cdu_consent_v1');
    if (!memoryCookie) {
      return null;
    }
    return JSON.parse(memoryCookie).status || 'pending';
  } catch (error) {
    return null;
  }
}

/**
 * Resolves the current CDU consent status, preferring real cookies and falling
 * back to in-memory storage; defaults to `pending` when absent or unparseable.
 *
 * @returns The consent status: `pending`, `accepted`, or `rejected`.
 */
function getConsentStatus(): 'pending' | 'accepted' | 'rejected' {
  const status = readDocumentConsentStatus() ?? readMemoryConsentStatus() ?? 'pending';
  return status as 'pending' | 'accepted' | 'rejected';
}

/**
 * @returns `true` when CDU consent has been explicitly accepted.
 */
export function hasConsent(): boolean {
  return getConsentStatus() === 'accepted';
}
